import { useEffect, useMemo, useState } from 'react';
import { frameNaarSeconde, type ProjectModel } from '@video-studio/shared';
import { api, ApiFoutException, type Taak } from '../../api/client';
import type { useEditor } from '../../state/useEditor';

/**
 * Ondertiteling.
 *
 * De transcriptie loopt als achtergrondtaak bij een externe provider. Zodra die
 * klaar is, ziet de gebruiker de segmenten en kan hij ze als tekstlagen op de
 * tijdlijn zetten. Daarna zijn het gewone tekstlagen: aanpasbaar in het
 * eigenschappenpaneel en met één klik ongedaan te maken.
 */
export interface OndertitelPaneelProps {
  editor: ReturnType<typeof useEditor>;
}

interface Segment {
  startSeconden: number;
  eindSeconden: number;
  tekst: string;
}

interface Status {
  geconfigureerd: boolean;
  provider: string | null;
  model: string | null;
  ontbrekend: string[];
  prijsPerMinuutUsd: number | null;
}

export function OndertitelPaneel({ editor }: OndertitelPaneelProps) {
  const [status, setStatus] = useState<Status | null>(null);
  const [assetId, setAssetId] = useState('');
  const [taak, setTaak] = useState<Taak | null>(null);
  const [segmenten, setSegmenten] = useState<Segment[]>([]);
  const [fout, setFout] = useState<string | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const [fontSize, setFontSize] = useState(44);
  const [offset, setOffset] = useState(0);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    void fetch('/v1/ai/transcriptie/status')
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => undefined);
  }, []);

  // Media met geluid: video's en audiobestanden.
  const kandidaten = useMemo(
    () => editor.media.filter((m) => m.inspectie?.heeftAudio || m.soort === 'audio'),
    [editor.media],
  );

  const gekozen = kandidaten.find((m) => m.id === assetId) ?? null;
  const duurSeconden = gekozen?.inspectie?.duurSeconden ?? null;
  const raming =
    status?.prijsPerMinuutUsd && duurSeconden !== null
      ? (duurSeconden / 60) * status.prijsPerMinuutUsd
      : null;

  // Pollen zolang de taak loopt.
  useEffect(() => {
    if (!taak || taak.status === 'klaar' || taak.status === 'mislukt') return;
    const id = window.setTimeout(async () => {
      try {
        const bijgewerkt = await api.taak(taak.id);
        setTaak(bijgewerkt);
        if (bijgewerkt.status === 'klaar') {
          const resultaat = bijgewerkt.resultaat as
            | { segmenten?: Segment[]; taal?: string; provider?: string }
            | null;
          setSegmenten(resultaat?.segmenten ?? []);
          setMelding(
            `Transcriptie klaar: ${resultaat?.segmenten?.length ?? 0} segmenten${
              resultaat?.taal ? ` (taal ${resultaat.taal})` : ''
            } via ${resultaat?.provider ?? status?.provider ?? 'de provider'}.`,
          );
        }
        if (bijgewerkt.status === 'mislukt') {
          setFout(bijgewerkt.foutMelding ?? 'De transcriptie is mislukt.');
        }
      } catch {
        // Volgende ronde opnieuw.
      }
    }, 1500);
    return () => window.clearTimeout(id);
  }, [taak, status?.provider]);

  const start = async () => {
    if (!assetId) {
      setFout('Kies eerst een mediabestand met gesproken tekst.');
      return;
    }
    if (raming !== null) {
      const ok = window.confirm(
        `Dit start spraakherkenning bij ${status?.provider}.\n\n` +
          `Bestand: ${gekozen?.naam}\nDuur: ${duurSeconden?.toFixed(1)} s\n` +
          `Geraamde kosten: ± $${raming.toFixed(4)}\n\nDoorgaan?`,
      );
      if (!ok) return;
    }
    setBezig(true);
    setFout(null);
    setMelding(null);
    setSegmenten([]);
    try {
      const respons = await fetch('/v1/ai/transcripties', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, taal: 'nl' }),
      });
      const data = await respons.json();
      if (!respons.ok) {
        throw new ApiFoutException(
          respons.status,
          data?.fout?.code ?? 'TRANSCRIPTIE',
          data?.fout?.melding ?? 'De transcriptie kon niet worden gestart.',
        );
      }
      const nieuweTaak = await api.taak(data.taakId);
      setTaak(nieuweTaak);
      if (data.alGedaan && data.resultaat) {
        setSegmenten((data.resultaat as { segmenten?: Segment[] }).segmenten ?? []);
        setMelding('Deze transcriptie was al gedaan; het resultaat is hergebruikt zonder nieuwe kosten.');
      } else {
        setMelding('Transcriptie gestart. Dit kan een minuut duren.');
      }
    } catch (f) {
      setFout(f instanceof Error ? f.message : 'Er ging iets mis.');
    } finally {
      setBezig(false);
    }
  };

  const zetOpTijdlijn = async () => {
    if (!taak || !editor.project || segmenten.length === 0) return;
    setBezig(true);
    setFout(null);
    try {
      const respons = await fetch('/v1/ai/ondertitels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taakId: taak.id,
          projectId: editor.project.id,
          verwachteVersie: editor.versie,
          offsetSeconden: offset,
          fontSize,
        }),
      });
      const data = await respons.json();
      if (!respons.ok) {
        throw new ApiFoutException(
          respons.status,
          data?.fout?.code ?? 'ONDERTITEL',
          data?.fout?.melding ?? 'De ondertitels konden niet worden toegevoegd.',
        );
      }
      const nieuwProject = data.project as ProjectModel;
      editor.overschrijf(nieuwProject, `Ondertiteling: ${data.toegevoegd} regels`);
      setMelding(`${data.toegevoegd} ondertitelregels op de tijdlijn gezet. Pas ze aan via het paneel rechts.`);
    } catch (f) {
      setFout(f instanceof Error ? f.message : 'Er ging iets mis.');
    } finally {
      setBezig(false);
    }
  };

  const nietGeconfigureerd = status && !status.geconfigureerd;

  return (
    <div className="ondertitelpaneel">
      <h3>Automatische ondertiteling</h3>
      <p className="paneel__hint">
        Herkent spraak met tijdstempels en zet die om in tekstlagen. Daarna zijn het gewone
        tekstlagen: aanpasbaar en met één klik ongedaan te maken.
      </p>

      {nietGeconfigureerd && (
        <div className="melding melding--waarschuwing melding--klein">
          <strong>Nog niet ingesteld.</strong> De integratie is gebouwd, maar{' '}
          <code>{status?.ontbrekend.join(', ')}</code> ontbreekt op de server. Zet die in het
          <code>.env</code>-bestand. Er zijn nog geen kosten gemaakt.
        </div>
      )}

      <label className="veld">
        <span>Bestand met spraak</span>
        <select value={assetId} onChange={(e) => setAssetId(e.target.value)}>
          <option value="">Kies een bestand…</option>
          {kandidaten.map((media) => (
            <option key={media.id} value={media.id}>
              {media.naam}
              {media.inspectie?.duurSeconden ? ` (${media.inspectie.duurSeconden.toFixed(0)} s)` : ''}
            </option>
          ))}
        </select>
      </label>

      {kandidaten.length === 0 && (
        <p className="paneel__hint">
          Er staat nog geen video of audio met geluid in de mediabibliotheek. Upload eerst een
          bestand via het tabblad Media.
        </p>
      )}

      {raming !== null && (
        <p className="kostenraming__detail">
          Geraamde kosten: ± ${raming.toFixed(4)} ({duurSeconden?.toFixed(1)} s × $
          {status?.prijsPerMinuutUsd}/minuut). Dit is een schatting; de werkelijke kosten worden
          apart geregistreerd.
        </p>
      )}

      <button
        type="button"
        className="knop knop--primair knop--breed"
        disabled={bezig || !assetId || Boolean(nietGeconfigureerd)}
        onClick={() => void start()}
      >
        {bezig ? 'Bezig…' : 'Spraak herkennen'}
      </button>

      {taak && taak.status !== 'klaar' && taak.status !== 'mislukt' && (
        <div className="voortgangsbalk">
          <div
            className="voortgangsbalk__vulling"
            style={{ width: `${Math.round((taak.voortgang ?? 0) * 100)}%` }}
          />
          <span>{taak.fase ?? 'bezig'} — {Math.round((taak.voortgang ?? 0) * 100)}%</span>
        </div>
      )}

      {fout && <div className="melding melding--fout melding--klein">{fout}</div>}
      {melding && <div className="melding melding--info melding--klein">{melding}</div>}

      {segmenten.length > 0 && (
        <>
          <div className="rij">
            <label className="veld veld--kort">
              <span>Lettergrootte</span>
              <input
                type="number"
                min={16}
                max={120}
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
              />
            </label>
            <label className="veld veld--kort">
              <span>Verschuiving (s)</span>
              <input
                type="number"
                step="0.1"
                value={offset}
                onChange={(e) => setOffset(Number(e.target.value))}
                title="Positief schuift alle ondertitels later, negatief eerder"
              />
            </label>
          </div>

          <div className="ondertitelpaneel__lijst">
            {segmenten.map((segment, index) => (
              <div key={index} className="ondertitelregel">
                <span className="ondertitelregel__tijd">
                  {segment.startSeconden.toFixed(1)}–{segment.eindSeconden.toFixed(1)} s
                </span>
                <span className="ondertitelregel__tekst">{segment.tekst}</span>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="knop knop--primair knop--breed"
            disabled={bezig}
            onClick={() => void zetOpTijdlijn()}
          >
            {segmenten.length} regels op de tijdlijn zetten
          </button>
          <p className="paneel__hint">
            Dit vervangt niets: de regels worden toegevoegd aan het tekstspoor. Je kunt ze daarna
            per regel verplaatsen, inkorten of verwijderen.
          </p>
        </>
      )}
    </div>
  );
}
